// covered-by: frontends/carrie/src/ask-v2/transport/__tests__/TextSelectionToolbar.test.tsx
import { type RefObject, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

type TextSelectionToolbarProps = {
  containerRef: RefObject<HTMLElement | null>;
  onAddContext: (input: { text: string; sourceMessageId?: string }) => void;
};

type SelectionAnchor = {
  text: string;
  sourceMessageId?: string;
  top: number;
  left: number;
};

function resolveMessageContentElement(node: Node | null): Element | null {
  if (!node) return null;
  if (node instanceof Element) {
    return node.closest('[data-testid^="chat-message-content-"]');
  }
  return (
    node.parentElement?.closest('[data-testid^="chat-message-content-"]') ??
    null
  );
}

function resolveSourceMessageId(contentEl: Element): string | undefined {
  const messageEl =
    contentEl.closest('[data-testid^="chat-message-user-"]') ??
    contentEl.closest('[data-testid^="chat-message-assistant-"]');
  const testId = messageEl?.getAttribute("data-testid") ?? "";
  const match = testId.match(/^chat-message-(?:user|assistant)-(.+)$/);
  return match?.[1];
}

export function TextSelectionToolbar({
  containerRef,
  onAddContext,
}: TextSelectionToolbarProps) {
  const { t } = useTranslation("ui");
  const [anchor, setAnchor] = useState<SelectionAnchor | null>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const updateFromSelection = () => {
      const selection = window.getSelection();
      if (
        !selection ||
        selection.isCollapsed ||
        selection.rangeCount === 0 ||
        !container.contains(selection.anchorNode)
      ) {
        setAnchor(null);
        return;
      }

      const text = selection.toString().trim();
      if (!text) {
        setAnchor(null);
        return;
      }

      const range = selection.getRangeAt(0);
      const contentEl = resolveMessageContentElement(
        range.commonAncestorContainer,
      );
      if (!contentEl || !container.contains(contentEl)) {
        setAnchor(null);
        return;
      }

      const rect = range.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();
      setAnchor({
        text,
        sourceMessageId: resolveSourceMessageId(contentEl),
        top: rect.bottom - containerRect.top + container.scrollTop + 4,
        left: rect.left - containerRect.left + container.scrollLeft,
      });
    };

    container.addEventListener("mouseup", updateFromSelection);
    document.addEventListener("keyup", updateFromSelection);
    return () => {
      container.removeEventListener("mouseup", updateFromSelection);
      document.removeEventListener("keyup", updateFromSelection);
    };
  }, [containerRef]);

  if (!anchor) return null;

  // why: v1 highlighted-text action is "Ask about this"; these keys name the composer action
  return (
    <button
      type="button"
      data-testid="ask-v2-add-selection-context"
      aria-label={t("askV2.transport.addSelectionContext")}
      className="absolute z-30 cursor-pointer rounded-md border border-puntt-neutral-gray-6 bg-base-white px-2 py-1 text-sm shadow-md hover:bg-puntt-neutral-gray-2"
      style={{ top: anchor.top, left: anchor.left }}
      onMouseDown={(event) => {
        event.preventDefault();
      }}
      onClick={() => {
        onAddContext({
          text: anchor.text,
          sourceMessageId: anchor.sourceMessageId,
        });
        window.getSelection()?.removeAllRanges();
        setAnchor(null);
      }}
    >
      {t("askV2.transport.addSelectionContextLabel")}
    </button>
  );
}
